import React, { useEffect, useRef, useState } from 'react';
import { ConsiderationSection } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { EM_DASH } from '../../../utils/flowOfFundsFormat';
import { ExpandedText } from '../../../containers/DataRequest/ExpandedText/ExpandedText';
import { useConsiderationAutosaveQueue } from './ConsiderationAutosaveQueueContext';

const COMMENT_CLAMP_LINES = 3;
const COMMENT_HEIGHT_PX = 52;
const COMMENT_MAX_LENGTH = 2000;

const ReadOnlyComment = ({ value, uniqueId }: { value: string | null; uniqueId: string }) => (
    <td>
        {value ? (
            <ExpandedText
                content={value}
                uniqueId={uniqueId}
                heightToShowLinksPx={COMMENT_HEIGHT_PX}
                clampLines={COMMENT_CLAMP_LINES}
                className="ff-detail__comment"
            />
        ) : (
            <span className="ff-detail__fixed">{EM_DASH}</span>
        )}
    </td>
);

export interface ConsiderationCommentCellProps {
    value: string | null;
    uniqueId: string;
    isEditable: boolean;
    section: ConsiderationSection;
    rowKey: number;
}

export const ConsiderationCommentCell = ({
    value,
    uniqueId,
    isEditable,
    section,
    rowKey,
}: ConsiderationCommentCellProps) => {
    const queue = useConsiderationAutosaveQueue();
    const pendingValue = queue.pendingComment(section, rowKey);
    const effectiveValue = pendingValue !== undefined ? pendingValue : value;
    const [draft, setDraft] = useState(effectiveValue ?? '');
    const isEditingRef = useRef(false);

    useEffect(() => {
        if (!isEditingRef.current) {
            setDraft(effectiveValue ?? '');
        }
    }, [effectiveValue]);

    if (!isEditable) {
        return <ReadOnlyComment value={value} uniqueId={uniqueId} />;
    }

    const commit = () => {
        isEditingRef.current = false;
        const trimmed = draft.trim();
        if (trimmed === (effectiveValue ?? '')) {
            return;
        }
        queue.setComment(section, rowKey, trimmed || null);
    };

    return (
        <td>
            <textarea
                className="form-control ff-detail__comment-input"
                value={draft}
                maxLength={COMMENT_MAX_LENGTH}
                onFocus={() => (isEditingRef.current = true)}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={commit}
            />
        </td>
    );
};
